import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import {
  Boxes, CheckCircle2, Key, Link2, Power, Pencil, Plus, Square, SquareCheck, Tag, Trash2, UploadCloud, UserPlus, XCircle,
} from "lucide-react";

import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, getLoggedInUserId, showToast, generateRandomNumber, formatDateTime, registeredUpdatedTitle} from "../../utils/utility";
import {DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS} from "../../utils/paginationUtils";
import {hasPermission} from "../../utils/rolePermissions";

import Button from "../../components/ui/Button";
import Pill from "../../components/ui/Pill";
import Avatar from "../../components/ui/Avatar";
import KebabMenu from "../../components/ui/KebabMenu";
import EmptyState from "../../components/ui/EmptyState";
import Spinner from "../../components/ui/Spinner";
import Modal from "../../components/ui/Modal";
import PageHeader from "../../components/layout/PageHeader";
import KpiTile, {KpiRow} from "../../components/data/KpiTile";
import FilterBar from "../../components/data/FilterBar";
import {tableStyles} from "../../components/data/dataTableTheme";
import Select from "../../components/ui/Select";
import {ASSET_TYPES, statusToTone, typeCode, typeColor, typeLabel} from "./assetMeta";
import {VEHICLE_TYPES} from "../../utils/vehicleIcons";
import AssetSlideOver from "./AssetSlideOver";
import AssetLabelsModal from "./AssetLabelsModal";
import AssetAssignSlideOver from "./AssetAssignSlideOver";
import BulkAllocateSlideOver from "./BulkAllocateSlideOver";
import BulkPasscodeSlideOver from "./BulkPasscodeSlideOver";
import BulkUploadSlideOver from "../../components/data/BulkUploadSlideOver";
import AllocationInsights from "./AllocationInsights";

/** Bulk-import template: column contract + per-cell validation. */
const ASSET_TYPE_VALUES = ASSET_TYPES.map((t) => t.value);
const BULK_ASSET_COLUMNS = [
  {key: "name", label: "Asset Name", required: true},
  {key: "assetUnitId", label: "Unit ID", required: true, unique: true,
    validate: (v) => (/\s/.test(v) ? "must not contain spaces" : null)},
  {key: "assetType", label: "Asset Type", required: true,
    // Accept either the enum value (GPS_LOCK) or the display label (GPS Lock)
    normalize: (v) => {
      const hit = ASSET_TYPES.find(
        (t) => t.value === v.toUpperCase().replace(/\s+/g, "_") || t.label.toLowerCase() === v.toLowerCase()
      );
      return hit ? hit.value : v;
    },
    validate: (v) => (ASSET_TYPE_VALUES.includes(v) ? null : `must be one of ${ASSET_TYPE_VALUES.join(", ")}`)},
  {key: "description", label: "Description", required: false},
  {key: "assetPassword", label: "Device Password", required: false,
    requiredIf: (row) => row.assetType === "GPS_LOCK",
    validate: (v, row) =>
      (row.assetType === "GPS_LOCK" && v.length !== 6 ? "must be exactly 6 characters" : null)},
  {key: "countryCode", label: "Country Code", required: false,
    requiredIf: (row) => row.assetType === "MOBI_TRACK",
    validate: (v) => (/^\+?\d{1,4}$/.test(v) ? null : "must be a dialling code like +91")},
  {key: "vehicleType", label: "Vehicle Segment", required: false,
    // Accept the enum value (GT06 trackers only) or the display label ("Truck / Lorry")
    normalize: (v) => {
      const hit = VEHICLE_TYPES.find(
        (t) => t.value === v.toUpperCase() || t.label.toLowerCase() === v.toLowerCase()
      );
      return hit ? hit.value : v.toUpperCase();
    },
    validate: (v, row) => {
      if (row.assetType !== "GT06_TRACKER") return "only applies to GPS Tracker assets";
      return VEHICLE_TYPES.some((t) => t.value === v)
        ? null
        : `must be one of ${VEHICLE_TYPES.map((t) => t.value).join(", ")}`;
    }},
];
const BULK_ASSET_SAMPLES = [
  {name: "Truck KA-01-AB-1234", assetUnitId: "6122012345", assetType: "GPS_LOCK", description: "North yard fleet", assetPassword: "123456", countryCode: ""},
  {name: "Container 14", assetUnitId: "8624080050", assetType: "BT_LOCK", description: "", assetPassword: "", countryCode: ""},
  {name: "Delivery Bike 7", assetUnitId: "8624080051", assetType: "GT06_TRACKER", description: "Last-mile", assetPassword: "", countryCode: "", vehicleType: "BIKE"},
  {name: "Driver Phone 3", assetUnitId: "9876543210", assetType: "MOBI_TRACK", description: "Sales route", assetPassword: "", countryCode: "+91"},
];

/**
 * Redesigned Asset List page (Phase 5).
 *
 * Replaces the previous 4-tab Asset page (List / Register / Allocate /
 * Passcode) with a single list. Create/Edit are slide-overs, allocate is a
 * row-contextual slide-over, suspend/activate hits the existing endpoint
 * with the same mid-trip-block confirmation flow as before.
 *
 * The original /assets route is preserved by the parent container which
 * mounts this component as the default view; Allocate/Passcode tabs can
 * still be accessed through their existing routes if linked, but the
 * common paths are surfaced contextually from row actions.
 */
export default function AssetList() {
  const [assets, setAssets] = useState([]);
  const [allForStats, setAllForStats] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Labels: catalogue for the client, a per-asset map (assetId → labels), and
  // the active label filter (list of selected labelIds).
  const [allLabels, setAllLabels] = useState([]);
  const [assetLabelsMap, setAssetLabelsMap] = useState({});
  const [filterLabelIds, setFilterLabelIds] = useState([]);
  const [labelEditor, setLabelEditor] = useState({open: false, asset: null});
  const [bulkLabel, setBulkLabel] = useState({open: false, name: "", saving: false});

  const [editor, setEditor] = useState({open: false, mode: "edit", asset: null});
  const [assignTarget, setAssignTarget] = useState(null);
  const [tripBlock, setTripBlock] = useState({open: false, tripName: null});

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkPasscodeOpen, setBulkPasscodeOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  const canViewActions = hasPermission("assets_viewActions");
  const canAllocate = hasPermission("assets_allocation");
  const canPasscode = hasPermission("assets_passcode");

  // ── Data fetching ─────────────────────────────────────────────
  // Every asset of the client in one call (no page/size → the server returns them all), paged in
  // the browser: the search, type, status and label filters below work on this list, so it must
  // be the whole fleet. Asking the server for one page here showed only the first 10 assets.
  const allAssetsUrl = () => `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/assets/${UrlConstants.GET_ALL_ASSETS}`;

  const fetchAssets = async () => {
    try {
      setIsLoading(true);
      setError(null);
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(allAssetsUrl());
      const data = Array.isArray(res.data?.responseData) ? res.data.responseData : [];
      setAssets(data);
      setAllForStats(data);
    } catch (e) {
      console.error(e);
      setError("Could not load assets");
    } finally {
      setIsLoading(false);
    }
  };

  // Quiet refresh of the counts (no loading state), after changes made in place in the list.
  const fetchAllForStats = async () => {
    try {
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(allAssetsUrl());
      const data = res.data?.responseData || [];
      if (Array.isArray(data)) setAllForStats(data);
    } catch {/* best-effort */}
  };

  // Fetch the client's label catalogue, then the per-asset label map. The map
  // is built in one call via /asset-labels/assets?labelIds=<all ids>, which
  // returns every asset carrying at least one label (assets with none simply
  // don't appear → treated as unlabelled). Avoids an N+1 per-asset fetch.
  const fetchLabels = async () => {
    try {
      const clientId = getClientIdOfUser();
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const labelsRes = await axios.get(
        `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${UrlConstants.ASSET_LABELS}`
      );
      const labels = labelsRes.data?.responseData || [];
      setAllLabels(Array.isArray(labels) ? labels : []);

      if (!labels.length) {
        setAssetLabelsMap({});
        return;
      }
      const idsParam = labels.map((l) => l.labelId).join(",");
      const assetsRes = await axios.get(
        `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${UrlConstants.ASSET_LABELS_ASSETS}`,
        {params: {labelIds: idsParam, page: 0, size: 1000}}
      );
      const rows = assetsRes.data?.responseData || [];
      const map = {};
      rows.forEach((r) => {
        if (r?.asset?.assetId != null) map[r.asset.assetId] = r.labels || [];
      });
      setAssetLabelsMap(map);
    } catch (e) {
      console.error("Failed to load asset labels", e);
    }
  };

  useEffect(() => {
    fetchAssets();
    fetchLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Mutations ─────────────────────────────────────────────────
  const toggleStatus = async (asset) => {
    const isSuspended = (asset.assetStatus || "").toUpperCase() === "SUSPENDED";

    if (!isSuspended) {
      // Suspend path — check active trip first (preserves the existing
      // mid-trip block flow from the original screen).
      try {
        const checkUrl = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.TRIP_ACTIVE_FOR_DEVICE(asset.assetUnitId)}`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const checkResp = await axios.get(checkUrl);
        if (checkResp.status === 200 && checkResp.data?.responseData) {
          const trip = checkResp.data.responseData;
          setTripBlock({open: true, tripName: trip.name || `Trip #${trip.id || trip.tripId}`});
          return;
        }
      } catch (err) {
        if (err.response?.status !== 404) {/* proceed */}
      }
    }

    try {
      const newStatus = isSuspended ? "ACTIVE" : "SUSPENDED";
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/assets`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const resp = await axios.put(url, null, {
        params: {assetId: asset.assetId, assetStatus: newStatus, userId: getLoggedInUserId()},
      });
      if (resp.status === 200) {
        setAssets((prev) => prev.map((a) => a.assetId === asset.assetId ? {...a, assetStatus: newStatus} : a));
        setAllForStats((prev) => prev.map((a) => a.assetId === asset.assetId ? {...a, assetStatus: newStatus} : a));
        showToast(`Asset ${isSuspended ? "activated" : "suspended"}`, {type: "success"});
      }
    } catch (err) {
      showToast(err?.response?.data?.responseMessage || "Status update failed", {type: "error"});
    }
  };

  // Admin-only soft delete — hides the asset everywhere; history is retained server-side.
  const canDelete = hasPermission("assets_delete");
  const deleteAsset = async (asset) => {
    if (!window.confirm(
      `Delete "${asset.name}"?\n\nThe asset will be removed from all lists. Its history is kept and an admin can re-add it later.`
    )) return;
    try {
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/assets/${asset.assetId}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const resp = await axios.delete(url, {params: {requesterId: getLoggedInUserId()}});
      if (resp.status === 200) {
        setAssets((prev) => prev.filter((a) => a.assetId !== asset.assetId));
        setAllForStats((prev) => prev.filter((a) => a.assetId !== asset.assetId));
        showToast("Asset deleted", {type: "success"});
      }
    } catch (err) {
      showToast(err?.response?.data?.responseMessage || "Could not delete asset", {type: "error"});
    }
  };

  const handleSaved = (next) => {
    if (editor.mode === "edit" && next?.assetId) {
      setAssets((prev) => prev.map((a) => (a.assetId === next.assetId ? {...a, ...next} : a)));
    } else {
      fetchAssets();
    }
  };

  // ── Stats ────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = allForStats.length;
    const byStatus = (s) => allForStats.filter((a) => (a.assetStatus || "").toUpperCase() === s).length;
    const byType = (t) => allForStats.filter((a) => a.assetType === t).length;
    return {
      total,
      active: byStatus("ACTIVE"),
      assigned: byStatus("ASSIGNED"),
      suspended: byStatus("SUSPENDED"),
      gpsLock: byType("GPS_LOCK"),
      btLock: byType("BT_LOCK"),
      mobiTrack: byType("MOBI_TRACK"),
      gt06: byType("GT06_TRACKER"),
    };
  }, [allForStats]);

  // ── Filtering ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (typeFilter && a.assetType !== typeFilter) return false;
      if (statusFilter && (a.assetStatus || "").toUpperCase() !== statusFilter) return false;
      if (filterLabelIds.length) {
        const ids = (assetLabelsMap[a.assetId] || []).map((l) => l.labelId);
        // OR semantics: keep assets carrying any of the selected labels.
        if (!filterLabelIds.some((id) => ids.includes(id))) return false;
      }
      if (!q) return true;
      return (
        (a.name || "").toLowerCase().includes(q) ||
        (a.assetUnitId || "").toLowerCase().includes(q) ||
        String(a.assetId || "").includes(q)
      );
    });
  }, [assets, search, typeFilter, statusFilter, filterLabelIds, assetLabelsMap]);

  const activeFilters = [
    typeFilter && {label: `Type: ${typeLabel(typeFilter)}`, onRemove: () => setTypeFilter("")},
    statusFilter && {label: `Status: ${statusFilter}`, onRemove: () => setStatusFilter("")},
    ...filterLabelIds.map((id) => {
      const name = allLabels.find((l) => l.labelId === id)?.labelName || `#${id}`;
      return {label: `Label: ${name}`, onRemove: () => setFilterLabelIds((prev) => prev.filter((x) => x !== id))};
    }),
    search && {label: `Search: "${search}"`, onRemove: () => setSearch("")},
  ].filter(Boolean);

  const clearAll = () => { setTypeFilter(""); setStatusFilter(""); setSearch(""); setFilterLabelIds([]); };

  // Audit trail for filter interactions (KPI tiles, selects, search, chips)
  useEffect(() => {
    console.debug("[Assets] Filters →", {type: typeFilter || "ALL", status: statusFilter || "ALL", search: search || "(none)"});
  }, [typeFilter, statusFilter, search]);

  // ── Bulk selection helpers ───────────────────────────────────
  const allFilteredIds = filtered.map((a) => a.assetId);
  const allSelected = filtered.length > 0 && allFilteredIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;
  const toggleSelect = (id) => {
    console.debug("[Assets] Row checkbox toggled →", id);
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAll = () => {
    console.info("[Assets] Select-all toggled →", allSelected ? "clearing selection" : `selecting ${allFilteredIds.length} assets`);
    setSelectedIds((prev) => (allSelected ? prev.filter((id) => !allFilteredIds.includes(id)) : [...new Set([...prev, ...allFilteredIds])]));
  };
  const clearSelection = () => setSelectedIds([]);

  const selectedAssets = useMemo(
    () => assets.filter((a) => selectedIds.includes(a.assetId)),
    [assets, selectedIds]
  );
  const selectionAllBtLock = selectedAssets.length > 0 && selectedAssets.every((a) => a.assetType === "BT_LOCK");

  const bulkToggleStatus = async (targetActive) => {
    console.info("[Assets] Bulk status change requested →", {action: targetActive ? "activate" : "suspend", count: selectedAssets.length});
    // Walk the list serially so trip-block (if any) is surfaced predictably.
    let ok = 0;
    for (const asset of selectedAssets) {
      const currentlySuspended = (asset.assetStatus || "").toUpperCase() === "SUSPENDED";
      if (targetActive && !currentlySuspended) continue;
      if (!targetActive && currentlySuspended) continue;
      try {
        const newStatus = targetActive ? "ACTIVE" : "SUSPENDED";
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/assets`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        await axios.put(url, null, {params: {assetId: asset.assetId, assetStatus: newStatus, userId: getLoggedInUserId()}});
        setAssets((prev) => prev.map((a) => a.assetId === asset.assetId ? {...a, assetStatus: newStatus} : a));
        ok++;
      } catch {/* per-asset failure ignored to keep loop going */}
    }
    showToast(`${ok} asset${ok === 1 ? "" : "s"} ${targetActive ? "activated" : "suspended"}`, {type: "success"});
    fetchAllForStats();
    clearSelection();
  };

  // Apply saved labels for a single asset back into the map (from the editor).
  const applyAssetLabels = (assetId, labels) => {
    setAssetLabelsMap((prev) => ({...prev, [assetId]: labels || []}));
    fetchLabels(); // a brand-new label may have been created → refresh catalogue
  };

  const submitBulkLabel = async () => {
    const name = bulkLabel.name.trim();
    if (!name || selectedIds.length === 0) return;
    try {
      setBulkLabel((s) => ({...s, saving: true}));
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.ASSET_LABELS_BULK_ADD}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      await axios.post(url, selectedIds, {params: {labelName: name}});
      showToast(`Label "${name}" added to ${selectedIds.length} asset${selectedIds.length === 1 ? "" : "s"}`, {type: "success"});
      setBulkLabel({open: false, name: "", saving: false});
      clearSelection();
      fetchLabels();
    } catch (err) {
      showToast(err?.response?.data?.responseMessage || "Could not add label", {type: "error"});
      setBulkLabel((s) => ({...s, saving: false}));
    }
  };

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => [
    ...(canViewActions ? [{
      name: (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleSelectAll(); }}
          className="inline-flex items-center justify-center h-5 w-5 rounded text-ink-500 hover:text-brand-700"
          aria-label={allSelected ? "Deselect all" : "Select all"}
        >
          {allSelected
            ? <SquareCheck className="h-5 w-5 text-brand-700" />
            : someSelected
              ? <SquareCheck className="h-5 w-5 text-brand-400" />
              : <Square className="h-5 w-5" />}
        </button>
      ),
      width: "48px",
      // center:true puts the select-all header button on the exact same
      // vertical axis as the row checkboxes (head/data cells otherwise
      // carry different horizontal padding).
      center: true,
      ignoreRowClick: true,
      cell: (row) => {
        const sel = selectedIds.includes(row.assetId);
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleSelect(row.assetId); }}
            className="inline-flex items-center justify-center h-5 w-5 rounded text-ink-400 hover:text-brand-700"
            aria-label={sel ? "Deselect row" : "Select row"}
          >
            {sel ? <SquareCheck className="h-5 w-5 text-brand-700" /> : <Square className="h-5 w-5" />}
          </button>
        );
      },
    }] : []),
    {
      name: "Asset",
      grow: 1.6, minWidth: "240px", sortable: true,
      selector: (row) => row.name || "",
      cell: (row) => (
        <div className="flex items-center gap-3 py-1 min-w-0">
          <Avatar label={typeCode(row.assetType)} color={typeColor(row.assetType)} size="md"/>
          <div className="min-w-0">
            <div className="font-medium text-ink-900 truncate" title={row.name}>{row.name}</div>
            <div className="text-xs text-ink-500 font-mono truncate">{row.assetUnitId} · ID {row.assetId}</div>
          </div>
        </div>
      ),
    },
    {
      name: "Type", width: "140px", sortable: true,
      selector: (row) => row.assetType,
      cell: (row) => <span className="text-ink-700">{typeLabel(row.assetType)}</span>,
    },
    {
      name: "Labels", grow: 1, minWidth: "160px",
      cell: (row) => {
        const rowLabels = assetLabelsMap[row.assetId] || [];
        if (!rowLabels.length) return <span className="text-ink-400">—</span>;
        const shown = rowLabels.slice(0, 3);
        const extra = rowLabels.length - shown.length;
        return (
          <div className="flex flex-wrap items-center gap-1 py-1" title={rowLabels.map((l) => l.labelName).join(", ")}>
            {shown.map((l) => (
              <span key={l.labelId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-100 text-brand-700 text-xs font-medium">
                <Tag className="h-3 w-3" />{l.labelName}
              </span>
            ))}
            {extra > 0 && <span className="text-xs text-ink-500">+{extra}</span>}
          </div>
        );
      },
    },
    {
      name: "Assigned to", grow: 1, minWidth: "180px", sortable: true,
      selector: (row) => row.assignedToUsername || row.assignedToFullName || row.assignedToEmail || "",
      cell: (row) => {
        const uid = row.assignedToUserId ?? row.assignedTo ?? null;
        const uname = row.assignedToUsername || row.assignedToFullName || row.assignedToEmail || null;
        if (!uid && !uname) return <span className="text-ink-400">—</span>;
        return (
          <div className="min-w-0">
            {uname && <div className="text-ink-900 font-medium truncate">{uname}</div>}
            {uid != null && <div className="text-xs text-ink-500 font-mono">ID {uid}</div>}
          </div>
        );
      },
    },
    {
      // Single date column: last edit if any, else registration. Hover shows both.
      name: "Updated", width: "150px", sortable: true,
      selector: (row) => row.updatedAt || row.createdAt || "",
      cell: (row) => (
        <span className="text-xs text-ink-600" title={registeredUpdatedTitle(row.createdAt, row.updatedAt)}>
          {formatDateTime(row.updatedAt || row.createdAt) || "—"}
        </span>
      ),
    },
    {
      name: "Status", width: "130px", sortable: true,
      selector: (row) => row.assetStatus,
      cell: (row) => (
        <Pill tone={statusToTone(row.assetStatus)} size="sm">{row.assetStatus || "—"}</Pill>
      ),
    },
    ...(canViewActions ? [{
      name: "", width: "56px", right: true, ignoreRowClick: true, allowOverflow: true,
      cell: (row) => {
        const isSuspended = (row.assetStatus || "").toUpperCase() === "SUSPENDED";
        return (
          <KebabMenu
            ariaLabel={`Actions for ${row.name}`}
            items={[
              {label: "Edit asset", icon: Pencil, onClick: () => setEditor({open: true, mode: "edit", asset: row})},
              {label: "Assign to user", icon: UserPlus, onClick: () => setAssignTarget(row)},
              {label: "Manage labels", icon: Link2, onClick: () => setLabelEditor({open: true, asset: row})},
              {divider: true},
              {
                label: isSuspended ? "Activate asset" : "Suspend asset",
                icon: Power,
                danger: !isSuspended,
                onClick: () => toggleStatus(row),
              },
              ...(canDelete
                ? [{label: "Delete asset", icon: Trash2, danger: true, onClick: () => deleteAsset(row)}]
                : []),
            ]}
          />
        );
      },
    }] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canViewActions, canDelete, selectedIds, allSelected, someSelected, filtered.length, assetLabelsMap]);

  return (
    <>
      <PageHeader
        title="Assets"
        subtitle="Manage trackers, locks, assignments, and labels."
        actions={
          <div className="flex items-center gap-2">
            {canPasscode && (
              <Button variant="secondary" icon={Key} onClick={() => setBulkPasscodeOpen(true)}>
                Passcodes
              </Button>
            )}
            {canAllocate && (
              <Button variant="secondary" icon={UserPlus} onClick={() => setBulkAssignOpen(true)}>
                Bulk allocate
              </Button>
            )}
            {canViewActions && (
              <Button variant="secondary" icon={UploadCloud} onClick={() => setBulkUploadOpen(true)}>
                Bulk upload
              </Button>
            )}
            {canViewActions && (
              <Button variant="primary" icon={Plus} onClick={() => setEditor({open: true, mode: "create", asset: null})}>
                Register asset
              </Button>
            )}
          </div>
        }
      />

      {/* Full-width, tight vertical rhythm — pulls the table above the fold */}
      <div className="px-4 lg:px-6 py-4 space-y-4">
        <KpiRow>
          <KpiTile
            label="Total assets" value={stats.total} icon={Boxes} tone="brand"
            active={!typeFilter && !statusFilter}
            onClick={() => { setTypeFilter(""); setStatusFilter(""); }}
          />
          <KpiTile
            label="Active" value={stats.active} icon={CheckCircle2} tone="success"
            active={statusFilter === "ACTIVE"}
            onClick={() => setStatusFilter(statusFilter === "ACTIVE" ? "" : "ACTIVE")}
          />
          <KpiTile
            label="Assigned" value={stats.assigned} icon={UserPlus} tone="info"
            active={statusFilter === "ASSIGNED"}
            onClick={() => setStatusFilter(statusFilter === "ASSIGNED" ? "" : "ASSIGNED")}
          />
          <KpiTile
            label="Suspended" value={stats.suspended} icon={XCircle} tone="danger"
            active={statusFilter === "SUSPENDED"}
            onClick={() => setStatusFilter(statusFilter === "SUSPENDED" ? "" : "SUSPENDED")}
          />
          {ASSET_TYPES.map((t) => (
            <KpiTile
              key={t.value}
              label={t.label}
              value={stats[
                t.value === "GPS_LOCK" ? "gpsLock"
                : t.value === "BT_LOCK" ? "btLock"
                : t.value === "MOBI_TRACK" ? "mobiTrack"
                : "gt06"
              ]}
              tone="neutral"
              active={typeFilter === t.value}
              onClick={() => setTypeFilter(typeFilter === t.value ? "" : t.value)}
            />
          ))}
        </KpiRow>

        {/* Allocation analytics (MoM point 12) — Admin + Manager */}
        {hasPermission("sidebar_analytics") && <AllocationInsights assets={allForStats} />}

        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search name, unit ID, or asset ID…"
          chips={activeFilters}
          onClearAll={activeFilters.length ? clearAll : null}
        >
          <div className="w-40">
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All types</option>
              {ASSET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </div>
          <div className="w-36">
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="ASSIGNED">Assigned</option>
              <option value="SUSPENDED">Suspended</option>
            </Select>
          </div>
          {allLabels.length > 0 && (
            <div className="w-44">
              {/* Additive picker: choosing a label adds it to the filter; selected
                  labels appear as removable chips in the FilterBar chip row. */}
              <Select
                value=""
                onChange={(e) => {
                  const id = Number(e.target.value);
                  if (id) setFilterLabelIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
                }}
              >
                <option value="">Filter by label…</option>
                {allLabels
                  .filter((l) => !filterLabelIds.includes(l.labelId))
                  .map((l) => (
                    <option key={l.labelId} value={l.labelId}>{l.labelName}</option>
                  ))}
              </Select>
            </div>
          )}
        </FilterBar>

        {selectedIds.length > 0 && (
          <div className="sticky top-2 z-raised flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5 bg-brand-700 text-white rounded-xl shadow-md animate-slide-in-up">
            <span className="text-base font-semibold">
              {selectedIds.length} selected
            </span>
            <span className="text-sm text-white/70">·</span>
            <button type="button" onClick={clearSelection} className="text-sm text-white/80 hover:text-white underline-offset-2 hover:underline">
              Clear
            </button>
            <div className="flex-1" />
            {canAllocate && (
              <Button variant="secondary" size="sm" icon={UserPlus} onClick={() => setBulkAssignOpen(true)}>
                Assign to user
              </Button>
            )}
            {canPasscode && selectionAllBtLock && (
              <Button variant="secondary" size="sm" icon={Key} onClick={() => setBulkPasscodeOpen(true)}>
                Generate passcodes
              </Button>
            )}
            {canViewActions && (
              <Button variant="secondary" size="sm" icon={Tag} onClick={() => setBulkLabel({open: true, name: "", saving: false})}>
                Add label
              </Button>
            )}
            {canViewActions && (
              <>
                <Button variant="secondary" size="sm" icon={CheckCircle2} onClick={() => bulkToggleStatus(true)}>
                  Activate
                </Button>
                <Button variant="secondary" size="sm" icon={Power} onClick={() => bulkToggleStatus(false)}>
                  Suspend
                </Button>
              </>
            )}
          </div>
        )}

        <DataTable
          columns={columns}
          data={filtered}
          pagination
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onChangeRowsPerPage={(size) => setPerPage(size)}
          responsive
          highlightOnHover
          progressPending={isLoading}
          progressComponent={
            <div className="py-12 flex items-center justify-center gap-3 text-ink-500">
              <Spinner size="md"/><span className="text-sm">Loading assets…</span>
            </div>
          }
          noDataComponent={
            <EmptyState
              icon={Boxes}
              title={activeFilters.length ? "No assets match your filters" : error ? "Could not load assets" : "No assets yet"}
              description={activeFilters.length ? "Try clearing some filters." : "Register your first tracker or lock to begin."}
              tone={error ? "error" : "default"}
              action={
                activeFilters.length ? (
                  <Button variant="secondary" onClick={clearAll}>Clear filters</Button>
                ) : (
                  canViewActions && (
                    <Button variant="primary" icon={Plus} onClick={() => setEditor({open: true, mode: "create", asset: null})}>
                      Register asset
                    </Button>
                  )
                )
              }
            />
          }
          keyField="assetId"
          customStyles={tableStyles}
        />
      </div>

      <AssetSlideOver
        open={editor.open}
        mode={editor.mode}
        asset={editor.asset}
        onClose={() => setEditor({open: false, mode: "edit", asset: null})}
        onSaved={handleSaved}
      />
      <AssetAssignSlideOver
        asset={assignTarget}
        open={!!assignTarget}
        onClose={() => setAssignTarget(null)}
        onAssigned={() => fetchAssets()}
      />

      <BulkAllocateSlideOver
        open={bulkAssignOpen}
        preselectedAssetIds={selectedIds}
        onClose={() => setBulkAssignOpen(false)}
        onAllocated={() => { fetchAssets(); clearSelection(); }}
      />

      <BulkPasscodeSlideOver
        open={bulkPasscodeOpen}
        preselectedAssetIds={selectedIds.length ? selectedIds : null}
        onClose={() => setBulkPasscodeOpen(false)}
      />

      <BulkUploadSlideOver
        entity="assets"
        title="Bulk upload assets"
        subtitle="Import trackers and locks from a CSV or Excel file."
        columns={BULK_ASSET_COLUMNS}
        sampleRows={BULK_ASSET_SAMPLES}
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        commitRow={async (row) => {
          // Single-asset endpoint (POST /api/v1/{clientId}/assets) per row so
          // duplicate-unit-id failures surface per line instead of failing the
          // whole batch. Same ApiRequest<ClientAssetRequest> envelope as the
          // "Register asset" slide-over.
          const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.ASSETS}`;
          axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
          await axios.post(url, {
            requestId: generateRandomNumber().toString(),
            clientId: getClientIdOfUser(),
            userId: getLoggedInUserId(),
            requestBody: {
              clientAsset: {
                name: row.name,
                description: row.description || "",
                assetType: row.assetType,
                assetUnitId: row.assetUnitId,
                assetPassword: row.assetPassword ? btoa(row.assetPassword) : "",
                vehicleType: row.vehicleType || null,
              },
              countryCode: row.countryCode || null,
            },
          });
        }}
        onDone={({ok}) => {
          if (ok > 0) {
            showToast(`${ok} asset${ok === 1 ? "" : "s"} imported`, {type: "success"});
            fetchAssets();
          }
        }}
      />

      <AssetLabelsModal
        open={labelEditor.open}
        asset={labelEditor.asset}
        allLabels={allLabels}
        currentLabels={labelEditor.asset ? (assetLabelsMap[labelEditor.asset.assetId] || []) : []}
        onClose={() => setLabelEditor({open: false, asset: null})}
        onSaved={applyAssetLabels}
      />

      <Modal
        open={bulkLabel.open}
        onOpenChange={(o) => !o && setBulkLabel({open: false, name: "", saving: false})}
        title="Add label to selected assets"
        subtitle={`${selectedIds.length} asset${selectedIds.length === 1 ? "" : "s"} selected`}
        size="sm"
      >
        <Modal.Body>
          <label className="block text-sm font-medium text-ink-700 mb-1.5">Label name</label>
          <input
            autoFocus
            value={bulkLabel.name}
            onChange={(e) => setBulkLabel((s) => ({...s, name: e.target.value}))}
            onKeyDown={(e) => { if (e.key === "Enter") submitBulkLabel(); }}
            list="bulk-label-suggestions"
            placeholder="e.g. North yard"
            className="block w-full h-9 px-3 text-base rounded-md bg-white border border-ink-200 shadow-inset text-ink-900 hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring focus:ring-offset-0"
          />
          <datalist id="bulk-label-suggestions">
            {allLabels.map((l) => <option key={l.labelId} value={l.labelName} />)}
          </datalist>
          <p className="mt-2 text-xs text-ink-500">
            Existing labels are added; a new name creates the label. This does not remove other labels already on the assets.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setBulkLabel({open: false, name: "", saving: false})} disabled={bulkLabel.saving}>Cancel</Button>
          <Button variant="primary" onClick={submitBulkLabel} loading={bulkLabel.saving} disabled={!bulkLabel.name.trim()}>Add label</Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={tripBlock.open}
        onOpenChange={(o) => !o && setTripBlock({open: false, tripName: null})}
        title="Cannot suspend asset"
        subtitle="This asset is on an active trip."
        size="sm"
      >
        <Modal.Body>
          <p className="text-sm text-ink-700">
            The asset is currently part of:
          </p>
          <p className="mt-2 text-base font-semibold text-info-700">{tripBlock.tripName}</p>
          <p className="mt-3 text-sm text-ink-500">
            Complete or cancel the trip before suspending this asset.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setTripBlock({open: false, tripName: null})}>OK, got it</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
